import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import Modal from '@/ui-components/Modal';
import Panel, { PanelClose } from '@/ui-components/Panel';
import Button from '@/ui-components/Button';
import Typography from '@/ui-components/Typography';
import Gem from '@/ui-components/Gem';
import Loading from '@/ui-components/Loading';
import Input from '@/ui-components/Input';
import { Tab, Tabs } from '@/ui-components/Tabs';

import useBem from '@/use/useBem';
import { userSelector, loadUser } from '@/framework/user';
import { GemType, Gems } from '@/framework/gem';
import * as storeApi from '@/api/store.service';
import type { StoreItem } from '@/api/store.service';
import SparksBadge from '@/ui-components/SparksBadge';

type StoreFilter = 'all' | 'sale' | 'owned';

const resolveGemType = (gem?: string): GemType => {
  if (gem && gem in Gems) {
    return gem as GemType;
  }
  return 'gold';
};

const StoreModal = () => {
  const navigate = useNavigate();
  const { bem } = useBem('store');
  const loggedIn = useRecoilValue(userSelector.loggedIn);
  const user = useRecoilValue(userSelector.user);
  const [items, setItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [buyingSlug, setBuyingSlug] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StoreFilter>('all');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const catalogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStore() {
      try {
        const data = await storeApi.getStore();
        if (!cancelled) setItems(data.items ?? []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStore();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return items.filter((item) => {
      if (filter === 'sale' && item.owned) return false;
      if (filter === 'owned' && !item.owned) return false;
      if (!needle) return true;

      return (
        item.name.toLowerCase().includes(needle) ||
        item.slug.toLowerCase().includes(needle)
      );
    });
  }, [items, query, filter]);

  useEffect(() => {
    if (catalogRef.current) {
      catalogRef.current.scrollTop = 0;
    }
  }, [query, filter]);

  const selectedSlugResolved = visible.some((item) => item.slug === selectedSlug)
    ? selectedSlug
    : visible[0]?.slug ?? null;
  const selected =
    visible.find((item) => item.slug === selectedSlugResolved) ?? null;

  const handleClose = () => {
    navigate('/');
  };

  const handleBuy = async (slug: string) => {
    const item = items.find((entry) => entry.slug === slug);
    if (!item?.can_buy || !user.emailVerified) return;

    setBuyingSlug(slug);
    try {
      await storeApi.buyItem(slug);
      await loadUser();
      const data = await storeApi.getStore();
      setItems(data.items ?? []);
    } catch {
      // keep current list
    } finally {
      setBuyingSlug(null);
    }
  };

  if (!loggedIn) {
    return <Navigate to="/" replace />;
  }

  const needsVerify = Boolean(selected && !selected.owned && !user.emailVerified);

  return (
    <Modal
      isOpen={true}
      isCentered
      onRequestClose={handleClose}
      contentClassName={bem('')}
    >
      <Panel>
        <PanelClose onClick={handleClose} />
        <div className={bem('head')}>
          <Typography variant="xl" className={bem('h')}>
            Store
          </Typography>
          <SparksBadge compact amount={user.sparks ?? 0} />
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            <Input
              className={bem('search')}
              variant="s"
              placeholder="Search gems"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              isClearable={Boolean(query)}
              onClear={() => setQuery('')}
            />

            <Tabs
              className={bem('tabs')}
              value={filter}
              onChange={(value) => setFilter(value as StoreFilter)}
            >
              <Tab value="all">All</Tab>
              <Tab value="sale">For sale</Tab>
              <Tab value="owned">Owned</Tab>
            </Tabs>

            <div className={bem('catalog')} ref={catalogRef}>
              {visible.length === 0 ? (
                <Typography variant="m" className={bem('empty')}>
                  {items.length === 0
                    ? 'No items available.'
                    : 'No gems match.'}
                </Typography>
              ) : (
                visible.map((item) => {
                  const gemType = resolveGemType(item.payload?.gem);
                  const isSelected = item.slug === selectedSlugResolved;

                  return (
                    <button
                      key={item.slug}
                      type="button"
                      className={bem('row', {
                        selected: isSelected,
                        owned: item.owned,
                      })}
                      onClick={() => setSelectedSlug(item.slug)}
                    >
                      <Gem type={gemType} size="small" />
                      <div className={bem('row-meta')}>
                        <Typography variant="m" className={bem('row-name')}>
                          {item.name}
                        </Typography>
                        <Typography variant="s" className={bem('row-price')}>
                          {item.price_sparks} sparks
                        </Typography>
                      </div>
                      {item.owned && (
                        <Typography variant="s" className={bem('row-status')}>
                          Owned
                        </Typography>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {selected && (
              <div className={bem('footer')}>
                <Typography variant="s" className={bem('footer-copy')}>
                  {needsVerify
                    ? 'Verify your email to purchase gems.'
                    : `${selected.name} · ${selected.price_sparks} sparks`}
                </Typography>
                {needsVerify ? (
                  <Button size="medium" to="/profile">
                    Verify to buy
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="medium"
                    disabled={!selected.can_buy}
                    loading={buyingSlug === selected.slug}
                    onClick={() => handleBuy(selected.slug)}
                  >
                    {selected.owned ? 'Owned' : 'Buy'}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </Panel>
    </Modal>
  );
};

export default StoreModal;
